import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../brand-colors';
import BackToDashboard from '../components/BackToDashboard';

function LogoUpload() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ horizontal: false, vertical: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [logos, setLogos] = useState({
    horizontal: null,
    vertical: null,
  });

  const horizontalInputRef = useRef(null);
  const verticalInputRef = useRef(null);

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      const res = await fetch('/api/brand/get-logos', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogos({
          horizontal: data.logo_horizontal,
          vertical: data.logo_vertical,
        });
      }
    } catch (err) {
      console.error('Failed to load logos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, logoType) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [logoType]: true }));
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('logo', file);
    formData.append('logo_type', logoType);

    try {
      const res = await fetch('/api/brand/upload-logo-file', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        setLogos(prev => ({ ...prev, [logoType]: data.logo_url }));
        setSuccess(`${logoType.charAt(0).toUpperCase() + logoType.slice(1)} logo uploaded!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload logo');
    } finally {
      setUploading(prev => ({ ...prev, [logoType]: false }));
    }
  };

  const hasBothLogos = logos.horizontal && logos.vertical;

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="mb-4">
            <h1>Upload Your Logos</h1>
            <p className="text-muted">
              Upload both horizontal (landscape) and vertical (stacked) versions of your logo.
              These will be used throughout your marketing materials.
            </p>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success">{success}</Alert>
          )}

          <Row>
            {/* Horizontal Logo */}
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header style={{ background: colors.greyLightest }}>
                  <strong>Horizontal Logo</strong>
                  <span className="text-muted ms-2 small">(landscape orientation)</span>
                </Card.Header>
                <Card.Body className="text-center">
                  <div
                    style={{
                      border: `2px dashed ${logos.horizontal ? colors.greenMid : colors.greyMid}`,
                      borderRadius: '8px',
                      padding: '2rem',
                      minHeight: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: logos.horizontal ? '#f8fff8' : colors.greyLightest,
                      cursor: 'pointer',
                    }}
                    onClick={() => horizontalInputRef.current?.click()}
                  >
                    {uploading.horizontal ? (
                      <Spinner animation="border" size="sm" />
                    ) : logos.horizontal ? (
                      <img
                        src={logos.horizontal}
                        alt="Horizontal logo"
                        style={{ maxWidth: '100%', maxHeight: '120px' }}
                      />
                    ) : (
                      <div className="text-muted">
                        <div style={{ fontSize: '2rem' }}>📐</div>
                        <div>Click to upload horizontal logo</div>
                        <small>Recommended: 350 x 180 pixels</small>
                        <br />
                        <small>PNG, JPEG, or SVG (max 2.5MB)</small>
                      </div>
                    )}
                  </div>
                  <input
                    ref={horizontalInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUpload(e.target.files[0], 'horizontal')}
                  />
                  {logos.horizontal && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => horizontalInputRef.current?.click()}
                    >
                      Replace
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Vertical Logo */}
            <Col md={6} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header style={{ background: colors.greyLightest }}>
                  <strong>Vertical Logo</strong>
                  <span className="text-muted ms-2 small">(stacked orientation)</span>
                </Card.Header>
                <Card.Body className="text-center">
                  <div
                    style={{
                      border: `2px dashed ${logos.vertical ? colors.greenMid : colors.greyMid}`,
                      borderRadius: '8px',
                      padding: '2rem',
                      minHeight: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: logos.vertical ? '#f8fff8' : colors.greyLightest,
                      cursor: 'pointer',
                    }}
                    onClick={() => verticalInputRef.current?.click()}
                  >
                    {uploading.vertical ? (
                      <Spinner animation="border" size="sm" />
                    ) : logos.vertical ? (
                      <img
                        src={logos.vertical}
                        alt="Vertical logo"
                        style={{ maxWidth: '100%', maxHeight: '120px' }}
                      />
                    ) : (
                      <div className="text-muted">
                        <div style={{ fontSize: '2rem' }}>📏</div>
                        <div>Click to upload vertical logo</div>
                        <small>Recommended: 180 x 350 pixels</small>
                        <br />
                        <small>PNG, JPEG, or SVG (max 2.5MB)</small>
                      </div>
                    )}
                  </div>
                  <input
                    ref={verticalInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUpload(e.target.files[0], 'vertical')}
                  />
                  {logos.vertical && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => verticalInputRef.current?.click()}
                    >
                      Replace
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Status */}
          <Card className={`shadow-sm ${hasBothLogos ? 'border-success' : ''}`}>
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                {hasBothLogos ? (
                  <span className="text-success">
                    <strong>Both logos uploaded!</strong> You're all set.
                  </span>
                ) : (
                  <span className="text-muted">
                    {!logos.horizontal && !logos.vertical && 'Upload both logos to continue'}
                    {logos.horizontal && !logos.vertical && 'Now upload your vertical logo'}
                    {!logos.horizontal && logos.vertical && 'Now upload your horizontal logo'}
                  </span>
                )}
              </div>
              <div>
                <Button
                  variant="outline-secondary"
                  className="me-2"
                  onClick={() => navigate('/command-center/onboarding')}
                >
                  Back to Onboarding
                </Button>
                {hasBothLogos && (
                  <Button
                    style={{
                      background: colors.goldMid,
                      border: 'none',
                      color: colors.greyDarkest,
                    }}
                    onClick={() => navigate('/command-center/onboarding')}
                  >
                    Continue
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <Alert variant="info" className="mt-4">
            <strong>Why both versions?</strong>
            <ul className="mb-0 mt-2">
              <li><strong>Horizontal:</strong> Used in headers, email signatures, wide banners</li>
              <li><strong>Vertical:</strong> Used in social media profiles, favicons, square spaces</li>
            </ul>
          </Alert>
        </Col>
      </Row>
    </Container>
  );
}

export default LogoUpload;
